import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { apiClient } from '@/api/client'
import { queryClient } from '@/api/queries'

/**
 * Auth store.
 *
 * Security posture (UI Spec §2.1, Tech Stack §4.5):
 *  - Access token (JWT, ~15-minute TTL) lives ONLY in memory (this ref).
 *    It must NEVER be persisted to localStorage or sessionStorage.
 *  - Refresh token is an HttpOnly / Secure / SameSite=Strict cookie issued by
 *    the Platform API — the browser sends it automatically on /auth/refresh.
 *  - `activeTenantId` is the currently-impersonated or default school tenant.
 *    Super Admin callers may leave this null; School Admin callers set it at
 *    login time via the tenant picker or the auto-bound claim.
 */

/**
 * Role strings returned by the Platform API.
 *
 * The canonical platform role is `platform_super_admin` — the JWT converter
 * maps that to Spring Security's `ROLE_PLATFORM_SUPER_ADMIN` authority.
 * `super_admin` is kept as an accepted alias so older tokens and tests that
 * used the short form still resolve.
 */
export type UserRole =
  | 'platform_super_admin'
  | 'super_admin'
  | 'platform_billing_officer'
  | 'school_admin'
  | 'support'
  | 'canteen_admin'
  | 'canteen_manager'
  | 'canteen_cashier'
  // Academic Suite (V279): backend role codes PRINCIPAL / DEPUTY_PRINCIPAL /
  // HEAD_OF_DEPARTMENT, lowercased on the wire like every other role. They
  // gate the /school/curriculum section. HEAD_OF_DEPARTMENT is deliberately
  // NOT scoped to a department yet — academic departments do not exist, see
  // the V279 migration header.
  | 'principal'
  | 'deputy_principal'
  | 'head_of_department'
  // TEACHER / HOMEROOM_TEACHER predate the Academic Suite (V041 seeds both)
  // but had no gated route until leave, which any staff member may request.
  | 'teacher'
  | 'homeroom_teacher'
  // Transport module (Slice 1 RBAC, V265): backend role code TRANSPORT_MANAGER
  // arrives lowercased on the wire. Used to gate the /school/transport section.
  | 'transport_manager'
  // Transport-only staff (Task 6, transport staff management): backend role
  // codes BUS_OPERATOR / DRIVER, lowercased on the wire like every other
  // role. These users carry ONLY one of these two roles (no school_admin /
  // transport_manager / platform role) and are gated to the minimal
  // `/transport/me` read-only landing rather than the full school surface.
  | 'bus_operator'
  | 'driver'
  // Returned for parent-portal users after the unified-auth migration; the
  // backend lowercases role codes from `tenant_user_role` (DevAuthController
  // ~L168) so the wire value is `"parent"`, not `"PARENT"`.
  | 'parent'

export interface AuthUser {
  id: string
  email: string
  displayName: string
  roles: UserRole[]
  // Permission codes derived from the user's role assignments (RBAC). Empty
  // for users whose role rows haven't been seeded; the platform falls back
  // to the hardcoded school_admin default in that case. Used by the
  // router's `requiredPermissions` guard at `router/index.ts`.
  permissions: string[]
  // Only populated for School Admin sessions; Super Admins may belong to many tenants.
  tenantId?: string
  // The SCHOOL's module entitlement — what it bought, not what this user may
  // do. Rides the same login/refresh payload as roles and permissions, so it
  // rehydrates on a hard reload with no extra request and can never be half
  // loaded relative to them. Used by `requiredCapabilities` in the router.
  capabilities?: string[]
  // The SCHOOL's modules (TRANSPORT, FINANCE, ...). Decides which menus it
  // sees; see `anyOfModules` in router/access.ts for why capabilities alone
  // are not enough.
  modules?: string[]
  // Minutes of inactivity before this session is signed out (the school's
  // setting, else the platform's 30). Null for a parent, who has no limit;
  // absent from a pre-rollout payload, which gets no timer. The server
  // enforces the same limit on /v1/auth/refresh.
  idleTimeoutMinutes?: number | null
}

/** Why the last session ended, when it was not the user's own choice. */
export type SignOutReason = 'idle'

const SUPER_ADMIN_ROLES: readonly UserRole[] = ['platform_super_admin', 'super_admin']
const SCHOOL_ADMIN_ROLES: readonly UserRole[] = ['school_admin']
const CANTEEN_ADMIN_ROLES: readonly UserRole[] = ['canteen_admin']
// Task 6: staff who hold ONLY a transport role (bus_operator/driver) and none
// of school_admin/transport_manager/the platform roles. Distinct from
// transport_manager, who gets the full /school/transport section.
const TRANSPORT_STAFF_ROLES: readonly UserRole[] = ['bus_operator', 'driver']

function hasAnyRole(user: AuthUser | null, roles: readonly UserRole[]): boolean {
  if (!user?.roles) return false
  return user.roles.some((r) => roles.includes(r))
}

// Subset check, NOT a blocklist: transport-only iff the user holds at least
// one transport-staff role AND every role they hold is a transport-staff
// role. A hand-maintained exclusion list (school_admin/transport_manager/
// platform roles only) previously missed canteen_admin/canteen_manager/
// canteen_cashier/support/platform_billing_officer/parent, so e.g.
// ['support', 'driver'] was wrongly classified transport-only and confined
// to /transport/me.
function isTransportOnlyUser(user: AuthUser | null): boolean {
  if (!user?.roles || user.roles.length === 0) return false
  return (
    user.roles.some((r) => TRANSPORT_STAFF_ROLES.includes(r)) &&
    user.roles.every((r) => TRANSPORT_STAFF_ROLES.includes(r))
  )
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null)
  const user = ref<AuthUser | null>(null)
  const activeTenantId = ref<string | null>(null)
  const isImpersonating = ref<boolean>(false)
  // When the access token was last issued — by login or by refresh. The idle
  // timer renews the session while the user is active so the server's
  // inactivity check (gap since last renewal) only fires for real idleness.
  const lastRenewedAt = ref<number>(0)
  const signOutReason = ref<SignOutReason | null>(null)
  watch(accessToken, (token) => {
    if (token !== null) {
      lastRenewedAt.value = Date.now()
      signOutReason.value = null
    }
  })

  const isAuthenticated = computed<boolean>(() => accessToken.value !== null)
  const isSuperAdmin = computed<boolean>(() => hasAnyRole(user.value, SUPER_ADMIN_ROLES))
  const isSchoolAdmin = computed<boolean>(() => hasAnyRole(user.value, SCHOOL_ADMIN_ROLES))
  const isCanteenAdmin = computed<boolean>(() => hasAnyRole(user.value, CANTEEN_ADMIN_ROLES))
  const isTransportOnly = computed<boolean>(() => isTransportOnlyUser(user.value))

  /**
   * Whether the SCHOOL holds a capability — for hiding a panel inside a page
   * whose route is open, like the dashboard's arrears card. Same rule as the
   * route guard: an unknown set (a pre-rollout session) counts as held, and
   * the server's 403 is the real control.
   */
  function hasCapability(capability: string): boolean {
    const held = user.value?.capabilities
    return held === undefined || held.includes(capability)
  }

  /** Whether the SCHOOL bought a module. Unknown counts as held, as above. */
  function hasModule(module: string): boolean {
    const held = user.value?.modules
    return held === undefined || held.includes(module)
  }

  // Tenant invariant: cache lifetime = single-tenant session.
  // Drop every query response whenever activeTenantId transitions so the next
  // render fetches fresh data. Triggers on login, logout, impersonation
  // start/stop, and tenant picker changes.
  let lastTenantId: string | null = null
  watch(
    () => activeTenantId.value,
    (next) => {
      if (next === lastTenantId) return
      queryClient.clear()
      lastTenantId = next
    },
    { immediate: true },
  )

  /**
   * Log in with email + password. Sets access token in memory and expects the
   * server to have set the refresh cookie. Stubbed — wire to BRD FR-AUTH-*.
   */
  async function login(email: string, password: string): Promise<void> {
    // TODO(FR-AUTH-01, FR-AUTH-02): POST /auth/login, handle MFA (S-SUP-001 / S-SA-001).
    const { data } = await apiClient.post<{
      accessToken: string
      user: AuthUser
      tenantId?: string
    }>('/v1/auth/login', { email, password })
    accessToken.value = data.accessToken
    user.value = data.user
    activeTenantId.value = data.tenantId ?? data.user.tenantId ?? null
  }

  /**
   * Silent refresh using the HttpOnly refresh cookie. Called by both the
   * axios 401 interceptor and the router beforeEach guard on hard refresh.
   *
   * <p>**Single-flight at the store level.** Two callers — the router guard
   * (direct call) and the axios interceptor (via attemptRefresh) — used to
   * fire two concurrent POSTs to {@code /v1/auth/refresh} on a hard refresh.
   * Because the server rotates the cookie (single-use), the second request
   * found the token already consumed and responded 401 + Set-Cookie clear.
   * The cookie was wiped, the user's next refresh would 401 immediately, and
   * they were bounced to login on the SECOND F5. The promise cache below
   * collapses concurrent calls onto one network request.
   *
   * <p>**Tenant rehydration.** The refresh response carries the user with
   * its {@code tenantId}; reinstate {@code activeTenantId} from it so any
   * query gated on the active tenant (chrome school name, dashboard panels)
   * runs again post-refresh.
   */
  let refreshInFlight: Promise<string | null> | null = null

  async function refresh(): Promise<string | null> {
    if (refreshInFlight) return refreshInFlight
    refreshInFlight = (async () => {
      try {
        const { data } = await apiClient.post<{ accessToken: string; user?: AuthUser }>(
          '/v1/auth/refresh',
          {},
          // Bypass the request interceptor's token attachment — refresh relies on the cookie.
          { headers: { 'X-Skip-Auth': '1' } },
        )
        accessToken.value = data.accessToken
        if (data.user) {
          user.value = data.user
          activeTenantId.value = data.user.tenantId ?? activeTenantId.value
        }
        return data.accessToken
      } catch (err) {
        // The server ends a session left unused past the school's limit.
        if ((err as { code?: string } | null)?.code === 'session_idle') {
          await logout({ silent: true, reason: 'idle' })
        } else {
          await logout({ silent: true })
        }
        return null
      } finally {
        // Release the latch on the next microtask so concurrent awaits
        // resolve against the SAME completed promise rather than scheduling
        // a brand-new round-trip immediately after.
        queueMicrotask(() => {
          refreshInFlight = null
        })
      }
    })()
    return refreshInFlight
  }

  async function logout(
    options: { silent?: boolean; reason?: SignOutReason } = {},
  ): Promise<void> {
    if (!options.silent) {
      try {
        await apiClient.post('/v1/auth/logout', {})
      } catch {
        // Always clear local state even if the network call fails.
      }
    }
    // Belt-and-braces: the watcher does NOT fire on null → null transitions
    // (e.g. an idle logout where activeTenantId was already null), so the
    // explicit clear here guarantees we never carry a previous session's
    // cache forward.
    queryClient.clear()
    accessToken.value = null
    user.value = null
    activeTenantId.value = null
    isImpersonating.value = false
    signOutReason.value = options.reason ?? null
  }

  /**
   * Super Admin action — begin impersonating a tenant (S-SUP-050).
   * The server issues a scoped token; we swap ours and surface the banner.
   */
  async function startImpersonation(tenantId: string, reason: string): Promise<void> {
    // TODO(FR-SUP-05): POST /auth/impersonate { tenantId, reason }
    const { data } = await apiClient.post<{ accessToken: string }>('/auth/impersonate', {
      tenantId,
      reason,
    })
    accessToken.value = data.accessToken
    activeTenantId.value = tenantId
    isImpersonating.value = true
  }

  async function endImpersonation(): Promise<void> {
    // TODO(FR-SUP-05): POST /auth/impersonate/end; server restores prior session.
    await apiClient.post('/auth/impersonate/end', {})
    isImpersonating.value = false
    activeTenantId.value = null
    // Caller should route back to the platform dashboard and trigger a refresh.
    await refresh()
  }

  return {
    accessToken,
    user,
    activeTenantId,
    isImpersonating,
    lastRenewedAt,
    signOutReason,
    isAuthenticated,
    isSuperAdmin,
    isSchoolAdmin,
    isCanteenAdmin,
    isTransportOnly,
    hasCapability,
    hasModule,
    login,
    refresh,
    logout,
    startImpersonation,
    endImpersonation,
  }
})
